import { ReactNode, useCallback } from 'react';

import { commonTranslationKeys } from '@dreryk/react-common';
import { dispatchSnackbarNegativeEvent, ModalWidth, ShowModalEventInterceptor } from '@dreryk/react-components';

import { hasReimbursementFull } from '~libs.shared.prescription';

import { draftPrescriptionsTranslationKeys } from '../../../../../translations';
import { DraftPrescriptionsFieldArray } from '../DraftPrescriptionsForm.types';

import { NfzDepartmentForm } from './NfzDepartmentForm';
import {
   useCurrentHealthcareProviderNfzConfigurationSetCommand,
   useNfzDepartmentFormController,
} from './NfzDepartmentForm.hooks';
import { useCqrsFindHealthcareProviderNfzConfiguration } from './useCqrsFindNfzConfiguration';
import { useNfzConfigurationNecessaryChecker } from './useNfzConfigurationNecessaryChecker';

const hasEveryStandardPrescriptionReimbursementFull = (draftPrescriptions: DraftPrescriptionsFieldArray): boolean =>
   draftPrescriptions.every((prescription) => hasReimbursementFull(prescription.data.reimbursement));

type NfzDepartmentUpdateEventInterceptorProps = {
   readonly draftPrescriptions: DraftPrescriptionsFieldArray;
   readonly children: ReactNode;
};

export const NfzDepartmentUpdateEventInterceptor = (props: NfzDepartmentUpdateEventInterceptorProps): JSX.Element => {
   const { children, draftPrescriptions } = props;
   const checkNfzConfigurationNecessary = useNfzConfigurationNecessaryChecker();
   const showModalCondition = useCallback(
      () => checkNfzConfigurationNecessary() && !hasEveryStandardPrescriptionReimbursementFull(draftPrescriptions),
      [checkNfzConfigurationNecessary, draftPrescriptions]
   );

   const { refetch: refetchNfzConfiguration } = useCqrsFindHealthcareProviderNfzConfiguration();
   const { executeAsync: setNfzConfiguration } = useCurrentHealthcareProviderNfzConfigurationSetCommand({
      onSuccess: refetchNfzConfiguration,
   });
   const { register, handleSubmit, formState } = useNfzDepartmentFormController();

   const handleCancel = useCallback(() => {
      dispatchSnackbarNegativeEvent(draftPrescriptionsTranslationKeys.addNfzDepartmentOrChangeReimbursement);
   }, []);

   return (
      <ShowModalEventInterceptor
         wrapInForm
         shouldShowModal={showModalCondition}
         onSubmit={async (closeModal) =>
            handleSubmit(async (data) => {
               await setNfzConfiguration(data);
               closeModal();
            })()
         }
         title={draftPrescriptionsTranslationKeys.nfzData}
         cancelButtonLabel={commonTranslationKeys.cancel}
         confirmationButtonLabel={draftPrescriptionsTranslationKeys.saveAndSend}
         content={<NfzDepartmentForm register={register} formState={formState} />}
         onCancel={handleCancel}
         modalWidth={ModalWidth.NARROW}
         testId={'nfzConfigurationCreate'}
      >
         {children}
      </ShowModalEventInterceptor>
   );
};
